import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCircleCheck,
  FaAward,
  FaLock,
  FaClock,
} from 'react-icons/fa6';
import CoursePlayerHeader from '../components/CoursePlayerHeader';
import { fetchJSON, postJSON } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSelfCheckProgress } from '../lib/selfCheckProgress';
import {
  getAttempts,
  getRotationHistory,
  recordAttempt,
  getCooldownRemainingMs,
  formatCooldown,
} from '../lib/finalQuizHistory';

// /courses/:slug/final-quiz
//
// 18-question end-of-course assessment drawn (with smart rotation) from a
// 30-question pool, balanced 3 per skill across the six skills. Pass at
// 70%+ to unlock the certificate.
//
// Stages:
//   intro    — explain the format, surface any cooldown / engagement-gate
//   quiz     — one question at a time, can navigate prev/next, can change
//              answers freely; last "Next" submits the whole batch
//   results  — overall score + per-skill breakdown only. Question-level
//              correctness is INTENTIONALLY hidden so learners can't memorize
//              the pool by repeatedly failing.
//
// Engagement gate (visible-but-locked):
//   The quiz CAN be reached unconditionally so reviewers / curious learners
//   can see the format, but actually starting it requires all 48 inline
//   self-checks to be answered correctly. The lock is a UI gate; the server
//   wires the same gate in once auth lands and self-check completion is
//   readable from quiz_attempts.
//
// Cooldown:
//   24h after any FAILED attempt. The retake button on the results page
//   shows the remaining time and is disabled until it elapses. Passing
//   imposes no cooldown (the certificate is unlocked anyway).

const STAGE_INTRO = 'intro';
const STAGE_QUIZ = 'quiz';
const STAGE_RESULTS = 'results';

export default function CourseFinalQuiz() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    document.title = 'Final Assessment — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  // ?from= is set by the sidebar link so "Back to course" returns the user
  // to the lesson they came from, not the public marketing page.
  const backHref = searchParams.get('from') || `/courses/${slug}/module-1/intro`;
  const selfCheck = useSelfCheckProgress(slug);
  const { user } = useAuth();

  const [questions, setQuestions] = useState(null);
  const [passingPercent, setPassingPercent] = useState(70);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // The cooldown is computed from localStorage and recomputed every minute
  // while we're in the intro/results stage so the countdown stays fresh
  // without forcing a manual reload.
  const [cooldownMs, setCooldownMs] = useState(() => getCooldownRemainingMs(slug));

  const [stage, setStage] = useState(() => {
    const attempts = getAttempts(slug);
    return attempts.length > 0 ? STAGE_RESULTS : STAGE_INTRO;
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // question_id -> selected_index
  // True if the server confirmed a prior passing attempt — used as a
  // fallback alongside localStorage so "Open my certificate" shows on a
  // failed retake even when localStorage was cleared between sessions.
  const [serverHasPriorPass, setServerHasPriorPass] = useState(false);
  const [results, setResults] = useState(() => {
    const attempts = getAttempts(slug);
    if (attempts.length === 0) return null;
    // Prefer the most recent passing attempt; fall back to the most recent attempt overall.
    // getAttempts returns newest-first, so [0] is always the most recent.
    const toShow = attempts.find((a) => a.passed) || attempts[0];
    return { score: toShow.score, total: toShow.total, percent: toShow.percent, passed: !!toShow.passed, breakdown: toShow.breakdown || [] };
  });
  const [scoring, setScoring] = useState(false);
  const scoringRef = useRef(false);
  const [scoringError, setScoringError] = useState(null);
  // Server-issued single-use token tying this quiz session to the exact 18
  // questions the server picked. Required by /final/score — without it the
  // server rejects the submission. Lives in a ref (not state) so the submit
  // handler reads the current value without stale-closure risk and so the
  // token isn't part of any rendered output.
  const attemptTokenRef = useRef(null);
  // Opaque rotation_history blob used by /attempt at start time AND echoed
  // back to /score so the server can compute the next blob from the union
  // of prior + this attempt. Lives in a ref so /score can read it after
  // rerenders without a stale-closure hazard.
  const rotationHistoryRef = useRef(null);

  // Re-tick the cooldown clock once a minute. Cheap and the page is short-
  // lived so we don't bother with anything fancier.
  useEffect(() => {
    const t = setInterval(() => setCooldownMs(getCooldownRemainingMs(slug)), 60000);
    return () => clearInterval(t);
  }, [slug]);

  // When authenticated, fetch the authoritative attempt history from the
  // server. This overrides the localStorage initialisation so pass/fail
  // state and cooldown are accurate across devices.
  useEffect(() => {
    if (!user || !slug) return;
    fetchJSON(`/api/quizzes/course/${slug}/final/history`)
      .then((data) => {
        if (!data || !Array.isArray(data.attempts) || data.attempts.length === 0) return;
        const attempts = data.attempts; // newest-first from server
        // Track whether the learner has ever passed (for the cert CTA on a
        // failed retake, even if localStorage was cleared).
        if (attempts.some((a) => a.passed)) setServerHasPriorPass(true);
        // Derive cooldown from the server's most-recent attempt.
        const latest = attempts[0];
        if (latest && !latest.passed) {
          const remaining = (24 * 60 * 60 * 1000) - (Date.now() - latest.ts);
          setCooldownMs(Math.max(0, remaining));
        } else if (latest && latest.passed) {
          setCooldownMs(0);
        }
        // Show the result from the server: prefer the most recent pass, fall
        // back to the most recent attempt overall.
        const toShow = attempts.find((a) => a.passed) || attempts[0];
        setResults({
          score: toShow.score,
          total: toShow.total,
          percent: toShow.percent,
          passed: !!toShow.passed,
          breakdown: toShow.breakdown || [],
        });
        setStage(STAGE_RESULTS);
      })
      .catch(() => {}); // fall back silently to localStorage initialisation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, slug]);

  // ─── Stage transitions ─────────────────────────────────────────────
  const handleStart = async () => {
    if (!selfCheck.allComplete) return;
    if (cooldownMs > 0) return;
    setLoading(true);
    setError(null);
    setAnswers({});
    setResults(null);
    setStepIdx(0);
    try {
      // Send the prior opaque rotation blob (if any) so the server can drive
      // smart rotation without ever trusting a raw client list of ids. We
      // also stash it in a ref so the /score call can echo it back later
      // and have the server compute a fresh blob from the union.
      const rotationHistory = getRotationHistory(slug);
      rotationHistoryRef.current = rotationHistory;
      const data = await postJSON(`/api/quizzes/course/${slug}/final/attempt`, {
        rotation_history: rotationHistory,
      });
      const list = Array.isArray(data.questions) ? data.questions : [];
      if (list.length === 0) {
        setError('The final assessment is being prepared. Please check back later.');
        setLoading(false);
        return;
      }
      setQuestions(list);
      // Stash the server-issued attempt token. Required at /score time —
      // without it the server (correctly) rejects the submission, so a
      // missing token means we need to bounce back to /attempt rather than
      // pretend everything is fine.
      attemptTokenRef.current = data.attempt_token || null;
      if (!attemptTokenRef.current) {
        setError('The server did not issue an attempt token. Please reload and try again.');
        setLoading(false);
        return;
      }
      if (data.passingPercent) setPassingPercent(data.passingPercent);
      setStage(STAGE_QUIZ);
    } catch (err) {
      setError(err.message || 'Could not load the assessment.');
    } finally {
      setLoading(false);
    }
  };

  const total = questions ? questions.length : 0;
  const current = stage === STAGE_QUIZ && questions ? questions[stepIdx] : null;
  const currentSelection = current ? answers[current.id] : null;

  const handleSelect = (idx) => {
    if (!current) return;
    setAnswers((prev) => ({ ...prev, [current.id]: idx }));
  };

  const handleNext = async () => {
    if (currentSelection == null) return;
    if (stepIdx < total - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    // Last question — submit the whole batch.
    if (scoringRef.current) return;
    scoringRef.current = true;
    setScoring(true);
    setScoringError(null);
    try {
      const payload = await postJSON(
        `/api/quizzes/course/${slug}/final/score`,
        {
          // The token binds this submission to the 18 ids the server picked
          // for this attempt. The server rejects /score without it (and burns
          // it once consumed), so a successful POST also closes the door on
          // any retry of THIS attempt — the user has to start a new one.
          attempt_token: attemptTokenRef.current,
          // Echo the prior rotation blob back so the server can union it
          // with this attempt's results when it signs the next blob. The
          // client never decodes either blob.
          rotation_history: rotationHistoryRef.current,
          answers: questions.map((q) => ({
            question_id: q.id,
            selected_index: answers[q.id],
          })),
        }
      );
      // Clear the token immediately on success — it's now burned server-side
      // and we don't want stale tokens lingering in memory for the lifetime
      // of the page if the user pokes around the results screen for a while.
      attemptTokenRef.current = null;
      // Stash the new server-issued rotation blob (still opaque) so an
      // immediate retake on the same page reuses it without a reload.
      rotationHistoryRef.current = payload.rotation_history || null;
      // Persist the attempt locally so cooldown + pass-status survive
      // reloads. The opaque rotation blob is stored alongside; no raw
      // question_ids ever touch localStorage.
      recordAttempt(slug, {
        ts: Date.now(),
        passed: !!payload.passed,
        score: payload.score,
        total: payload.total,
        percent: payload.percent,
        breakdown: payload.breakdown || [],
        rotation_history: payload.rotation_history || null,
      });
      setCooldownMs(getCooldownRemainingMs(slug));
      setResults({
        score: payload.score,
        total: payload.total,
        percent: payload.percent,
        passed: !!payload.passed,
        breakdown: payload.breakdown || [],
      });
      setStage(STAGE_RESULTS);
    } catch (err) {
      setScoringError(err.message || 'Failed to score the assessment.');
    } finally {
      setScoring(false);
      scoringRef.current = false;
    }
  };

  const handlePrev = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const handleRetake = () => {
    if (cooldownMs > 0) return;
    // Drop back to the intro stage; the next "Begin" call freshly fetches a
    // new attempt with the updated history (so smart rotation kicks in).
    setStage(STAGE_INTRO);
    setQuestions(null);
    setResults(null);
    setStepIdx(0);
    setAnswers({});
    setScoringError(null);
  };

  const progressPct = useMemo(() => {
    if (!total) return 0;
    return Math.round(((stepIdx + 1) / total) * 100);
  }, [stepIdx, total]);

  // ─── Loading / error / empty states ─────────────────────────────────
  if (loading) {
    return (
      <div className="container-page py-24">
        <div className="h-8 w-1/3 bg-ink/10 rounded animate-pulse" />
      </div>
    );
  }
  if (error && stage === STAGE_INTRO) {
    return (
      <div className="w-full bg-bone min-h-screen">
        <TopBar slug={slug} />
        <div className="container-page py-20 text-center">
          <h1 className="font-display text-2xl text-ink">{error}</h1>
          <Link to={backHref} className="btn-link mt-6 inline-flex">
            Back to course <FaArrowRight className="text-xs" />
          </Link>
        </div>
      </div>
    );
  }

  // ─── Main render ────────────────────────────────────────────────────
  return (
    <div className="w-full bg-bone min-h-screen">
      <TopBar slug={slug} backHref={backHref} stepIdx={stepIdx} total={total} stage={stage} progressPct={progressPct} />

      <div className="container-page py-12 lg:py-16">
        {stage === STAGE_INTRO && (
          <IntroPanel
            passingPercent={passingPercent}
            slug={slug}
            backHref={backHref}
            selfCheck={selfCheck}
            cooldownMs={cooldownMs}
            onStart={handleStart}
          />
        )}

        {stage === STAGE_QUIZ && current && (
          <QuestionPanel
            question={current}
            selected={currentSelection}
            stepIdx={stepIdx}
            total={total}
            onSelect={handleSelect}
            onPrev={handlePrev}
            onNext={handleNext}
            scoring={scoring}
            scoringError={scoringError}
          />
        )}

        {stage === STAGE_RESULTS && results && (
          <ResultsPanel
            results={results}
            passingPercent={passingPercent}
            slug={slug}
            backHref={backHref}
            cooldownMs={cooldownMs}
            onRetake={handleRetake}
            hasPriorPass={serverHasPriorPass || getAttempts(slug).some((a) => a.passed)}
          />
        )}
      </div>
    </div>
  );
}

function TopBar({ slug, backHref, stepIdx, total, stage, progressPct }) {
  const progress = stage === 'quiz' && total > 0
    ? `Question ${stepIdx + 1} of ${total}`
    : null;
  return (
    <CoursePlayerHeader
      courseTitle="Course Final Assessment"
      courseHref={backHref}
      progress={progress}
    />
  );
}

function IntroPanel({ passingPercent, slug, backHref, selfCheck, cooldownMs, onStart }) {
  const remaining = Math.max(0, selfCheck.total - selfCheck.count);
  const gateOpen = selfCheck.allComplete;
  const inCooldown = cooldownMs > 0;
  const canStart = gateOpen && !inCooldown;
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="eyebrow eyebrow-bar inline-flex">Final assessment</div>
      <h1 className="mt-5 font-display text-3xl lg:text-4xl text-ink leading-tight">
        Ready to demonstrate what you&rsquo;ve learned?
      </h1>
      <p className="mt-5 text-base text-slate2 leading-relaxed max-w-xl mx-auto">
        18 scenario questions covering all six skills from the course. Score{' '}
        <strong className="text-ink">{passingPercent}% or higher</strong> to
        unlock your Certificate of Completion.
      </p>

      {/* The six skills, surfaced as small chips so learners can preview
          what they'll be tested on. Mirrors the skill order used on the
          per-skill breakdown bars on the results page. Sourced inline
          rather than imported because this is the only intro that needs
          them; the canonical list lives in courseCertificate.js. */}
      <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
        {[
          'AI Literacy',
          'Critical Thinking',
          'Risk Awareness',
          'Bias & Misinformation',
          'Responsible AI Use',
          'Practical AI Use',
        ].map((skill) => (
          <span
            key={skill}
            className="text-[11px] tracking-wide uppercase font-medium text-brand-deep bg-brand-sage/30 border border-brand-deep/15 rounded-full px-3 py-1"
          >
            {skill}
          </span>
        ))}
      </div>

      <ul className="mt-8 text-sm text-slate2 space-y-2 max-w-md mx-auto text-left">
        <li className="flex items-start gap-3">
          <FaCircleCheck className="text-brand-moss mt-1 flex-shrink-0" />
          <span>One question at a time. You can review and change your answer before submitting.</span>
        </li>
        <li className="flex items-start gap-3">
          <FaCircleCheck className="text-brand-moss mt-1 flex-shrink-0" />
          <span>You&rsquo;ll see your overall score and a per-skill breakdown — no per-question reveal.</span>
        </li>
        <li className="flex items-start gap-3">
          <FaCircleCheck className="text-brand-moss mt-1 flex-shrink-0" />
          <span>Unlimited retakes — but if you don&rsquo;t pass, there&rsquo;s a 24-hour wait before your next attempt. Each attempt draws a fresh mix from a larger question pool.</span>
        </li>
      </ul>

      {/* Engagement gate — visible but locked until 48 self-checks done */}
      {!gateOpen && (
        <div
          role="status"
          className="mt-10 max-w-xl mx-auto rounded-2xl border border-brand-deep/20 bg-brand-deep/5 p-6 text-left"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-deep text-bone flex-shrink-0">
              <FaLock className="text-sm" />
            </span>
            <div className="min-w-0">
              <div className="font-display text-base text-ink">
                Final assessment locked
              </div>
              <p className="mt-1 text-sm text-slate2 leading-relaxed">
                Answer all <strong className="text-ink">{selfCheck.total}</strong> in-lesson
                Check-Your-Understanding questions correctly first. You have{' '}
                <strong className="text-ink">{remaining}</strong> remaining.
              </p>
              <div className="mt-3 h-1.5 bg-ink/10 rounded-full overflow-hidden max-w-xs">
                <div
                  className="h-full bg-brand-deep transition-all"
                  style={{ width: `${selfCheck.total ? Math.round((selfCheck.count / selfCheck.total) * 100) : 0}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-slate2">
                {selfCheck.count} of {selfCheck.total} complete
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cooldown notice (only renders if locked by cooldown, gate is open) */}
      {gateOpen && inCooldown && (
        <div
          role="status"
          className="mt-10 max-w-xl mx-auto rounded-2xl border border-amber-300 bg-amber-50 p-6 text-left"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-bone flex-shrink-0">
              <FaClock className="text-sm" />
            </span>
            <div className="min-w-0">
              <div className="font-display text-base text-ink">
                Cooldown in effect
              </div>
              <p className="mt-1 text-sm text-slate2 leading-relaxed">
                You can retake the assessment in{' '}
                <strong className="text-ink">{formatCooldown(cooldownMs)}</strong>.
                Use the time to revisit any modules you found tricky.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={onStart}
          disabled={!canStart}
          className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {gateOpen ? 'Begin assessment' : 'Locked'}
          <FaArrowRight className="ml-2 text-xs" />
        </button>
        <Link to={backHref} className="btn-link inline-flex">
          Back to course
        </Link>
      </div>
    </div>
  );
}

function QuestionPanel({ question, selected, stepIdx, total, onSelect, onPrev, onNext, scoring, scoringError }) {
  const isLast = stepIdx === total - 1;
  const labelId = `final-q-${question.id}-label`;
  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-4">
        <div className="text-[11px] tracking-[0.18em] uppercase text-brand-aubergine">
          Skill: <span className="text-brand-deep">{question.skill_label}</span>
        </div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate2 flex-shrink-0">
          {stepIdx + 1} / {total}
        </div>
      </div>
      <h2 id={labelId} className="mt-3 font-display text-2xl lg:text-3xl text-ink leading-snug">
        {question.question}
      </h2>

      {/* See QuizCheck.js for why `!list-none !pl-0` is needed even though
          the final-quiz page isn't itself inside .prose-lesson — defensive
          so this card behaves identically wherever it's embedded. */}
      <ul role="radiogroup" aria-labelledby={labelId} className="mt-7 space-y-3 !list-none !pl-0">
        {question.options.map((opt, idx) => {
          const isSelected = selected === idx;
          const cls = `flex items-start gap-3 px-5 py-4 rounded-xl border cursor-pointer transition-colors ${
            isSelected
              ? 'border-brand-deep bg-brand-deep/8 text-ink'
              : 'border-ink/15 bg-white text-ink hover:border-ink/30'
          }`;
          return (
            <li key={idx}>
              <label className={cls}>
                <input
                  type="radio"
                  name={`final-q-${question.id}`}
                  className="mt-1 accent-brand-deep"
                  checked={isSelected}
                  onChange={() => onSelect(idx)}
                />
                <span className="text-base leading-relaxed flex-1">{opt}</span>
              </label>
            </li>
          );
        })}
      </ul>

      {scoringError && (
        <div
          role="alert"
          className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3"
        >
          {scoringError}
        </div>
      )}

      <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
        <button
          type="button"
          onClick={onPrev}
          disabled={stepIdx === 0 || scoring}
          className="inline-flex items-center gap-2 text-sm text-slate2 hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FaArrowLeft className="text-[10px]" /> Previous
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={selected == null || scoring}
          className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {scoring ? 'Scoring…' : isLast ? 'Submit assessment' : 'Next question'}
          {!scoring && <FaArrowRight className="ml-2 text-xs" />}
        </button>
      </div>
    </div>
  );
}

function ResultsPanel({ results, passingPercent, slug, backHref, cooldownMs, onRetake, hasPriorPass }) {
  const { score, total, percent, passed, breakdown } = results;
  const inCooldown = cooldownMs > 0;
  return (
    <div className="max-w-3xl mx-auto">
      {/* Headline result */}
      <div
        className={`text-center rounded-2xl border-2 p-8 sm:p-10 ${
          passed
            ? 'border-brand-moss/40 bg-brand-moss/8'
            : 'border-amber-400/50 bg-amber-50'
        }`}
      >
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-aubergine">
          {passed ? 'Assessment passed' : 'Not quite — try again'}
        </div>
        <div className="mt-3 font-display text-5xl text-ink">
          {percent}%
        </div>
        <div className="mt-2 text-sm text-slate2">
          {score} of {total} correct · Passing score: {passingPercent}%
        </div>

        <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
          {passed ? (
            <>
              <Link
                to={`/courses/${slug}/certificate`}
                className="btn-primary inline-flex items-center"
              >
                <FaAward className="mr-2" /> Open my certificate
              </Link>
              <button
                type="button"
                onClick={onRetake}
                disabled={inCooldown}
                className="btn-link inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inCooldown ? `Retake in ${formatCooldown(cooldownMs)}` : 'Retake assessment'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onRetake}
                disabled={inCooldown}
                className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inCooldown
                  ? `Try again in ${formatCooldown(cooldownMs)}`
                  : 'Retake the assessment'}
              </button>
              {hasPriorPass && (
                <Link
                  to={`/courses/${slug}/certificate`}
                  className="btn-link inline-flex items-center"
                >
                  <FaAward className="mr-1.5" /> Open my certificate
                </Link>
              )}
            </>
          )}
          <Link to={backHref} className="btn-link inline-flex">
            Back to course
          </Link>
        </div>
        {!passed && inCooldown && (
          <p className="mt-4 text-xs text-slate2 max-w-md mx-auto">
            We use a short cooldown so each attempt is a fresh check rather
            than a memorization sprint. Use the time to revisit the modules
            where you scored lowest below.
          </p>
        )}
      </div>

      {/* Skill breakdown */}
      <div className="mt-12">
        <div className="eyebrow eyebrow-bar">Skills demonstrated</div>
        <h2 className="mt-4 font-display text-2xl text-ink">
          How you did across the six skills
        </h2>
        {breakdown.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {breakdown.map((b) => {
                const pct = b.total > 0 ? Math.round((b.correct / b.total) * 100) : 0;
                const strong = pct >= 70;
                const skillName = b.skill_label || b.skill;
                return (
                  <div
                    key={b.skill || skillName}
                    className="rounded-xl border border-ink/10 bg-white p-5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display text-base text-ink">{skillName}</div>
                      <div
                        className={`text-sm font-display ${
                          strong ? 'text-brand-moss' : 'text-amber-700'
                        }`}
                      >
                        {b.correct}/{b.total}
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 bg-ink/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          strong ? 'bg-brand-moss' : 'bg-amber-500'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {!passed && (
              <p className="mt-6 text-xs text-slate2 max-w-2xl">
                Focus your review on any skill below 70%. The next attempt will
                draw a fresh mix from the full question pool — including a re-test
                of any questions you missed this time.
              </p>
            )}
          </>
        ) : (
          <p className="mt-4 text-sm text-slate2">
            Take a new attempt to see your skill-by-skill breakdown.
          </p>
        )}
      </div>
    </div>
  );
}
